import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydSelect from '../../../../../commonDesign/SydSelect'

// Two compact selects ("X" and "Y") that the scatter tool registers
// into the panel toolbar's `actions` slot. Mutual exclusion: each
// SydSelect disables the value the OTHER axis currently holds, so the
// user can't accidentally pick the same measure for both axes.
//
// Mirrors the spacing of trend's ChartTypeToggle + FrequencyPicker so
// the toolbar reads as a peer row of compact controls regardless of
// which tool is active.

const useStyles = makeStyles(() => ({
  row: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8
  },
  label: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: 'var(--color-neutral-500, #888)'
  },
  axisGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  }
}))

function buildOptions (axisOptions, excludeId) {
  return axisOptions.map(opt => ({
    value: opt.columnInstance.id,
    label: opt.label,
    // SydSelect doesn't natively gate options; we disable post-render
    // via the option list shape if the underlying component supports
    // it. If not, the duplicate-pick is harmless (mutual exclusion
    // also enforced upstream in the tool's onChange handler).
    disabled: opt.columnInstance.id === excludeId
  }))
}

export default function AxisPicker ({ axisOptions, axisXId, axisYId, onAxisXChange, onAxisYChange }) {
  const classes = useStyles()

  const xOptions = useMemo(() => buildOptions(axisOptions, axisYId), [axisOptions, axisYId])
  const yOptions = useMemo(() => buildOptions(axisOptions, axisXId), [axisOptions, axisXId])

  return (
    <div className={classes.row}>
      <div className={classes.axisGroup}>
        <span className={classes.label}>X</span>
        <SydSelect
          options={xOptions}
          value={axisXId || ''}
          onChange={(e) => onAxisXChange(e.target.value)}
          size='xs'
          minWidth='150px'
        />
      </div>
      <div className={classes.axisGroup}>
        <span className={classes.label}>Y</span>
        <SydSelect
          options={yOptions}
          value={axisYId || ''}
          onChange={(e) => onAxisYChange(e.target.value)}
          size='xs'
          minWidth='150px'
        />
      </div>
    </div>
  )
}

AxisPicker.propTypes = {
  axisOptions: PropTypes.arrayOf(PropTypes.shape({
    columnInstance: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  axisXId: PropTypes.string,
  axisYId: PropTypes.string,
  onAxisXChange: PropTypes.func.isRequired,
  onAxisYChange: PropTypes.func.isRequired
}
